import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, LogOut, X, Loader2, ShieldCheck, Plus, ListChecks, KeyRound, Star, Pencil, Trash2, ChevronDown, UserCog, Inbox, Upload, CheckCircle2, Mail, Phone } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { LoginModal } from "@/components/AdminLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const photoSrc = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND}${u}`);
const inputCls = "w-full rounded-lg border border-white/12 bg-white/[0.04] px-2.5 py-1.5 text-sm text-white outline-none placeholder:text-slate-500 focus:border-amber-300/50";

export function AdminButton({ countries = [], onDataChanged, onEditCountry }) {
  const { isAdmin, logout } = useAuth();
  const [login, setLogin] = useState(false);
  const [menu, setMenu] = useState(false);
  const [modal, setModal] = useState(null); // 'add' | 'bulk' | 'password'

  if (!isAdmin) {
    return (
      <>
        <button data-testid="admin-login-open-btn" onClick={() => setLogin(true)}
          className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-slate-400 transition-colors hover:text-amber-300" title="Admin login">
          <Lock size={14} />
        </button>
        <LoginModal open={login} onClose={() => setLogin(false)} />
      </>
    );
  }

  const openModal = (m) => { setModal(m); setMenu(false); };

  return (
    <div className="relative flex items-center gap-2">
      <span data-testid="admin-editing-badge" className="hidden items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-300 sm:flex">
        <ShieldCheck size={12} /> Editing
      </span>
      <button data-testid="admin-menu-btn" onClick={() => setMenu((v) => !v)}
        className="flex items-center gap-1 rounded-lg border border-amber-300/30 bg-amber-300/10 px-2.5 py-1.5 text-[11px] font-semibold text-amber-200">
        Admin <ChevronDown size={13} className={`transition-transform ${menu ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {menu && (
          <>
            <div className="fixed inset-0 z-[55]" onClick={() => setMenu(false)} />
            <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              className="absolute right-0 top-10 z-[56] w-52 overflow-hidden rounded-xl border border-white/10 bg-[#0B0F17] py-1 shadow-2xl">
              <MenuItem testid="admin-add-open" icon={Plus} label="Add destination" onClick={() => openModal("add")} />
              <MenuItem testid="admin-bulk-open" icon={ListChecks} label="Manage destinations" onClick={() => openModal("bulk")} />
              <MenuItem testid="admin-inbox-open" icon={Inbox} label="Coordination requests" onClick={() => openModal("inbox")} />
              <MenuItem testid="admin-consultant-open" icon={UserCog} label="Consultant profile" onClick={() => openModal("consultant")} />
              <MenuItem testid="admin-password-open" icon={KeyRound} label="Change password" onClick={() => openModal("password")} />
              <div className="my-1 h-px bg-white/8" />
              <MenuItem testid="admin-logout-btn" icon={LogOut} label="Log out" onClick={() => { logout(); setMenu(false); }} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AddDestinationModal open={modal === "add"} onClose={() => setModal(null)} onDataChanged={onDataChanged} />
      <BulkManagerModal open={modal === "bulk"} onClose={() => setModal(null)} countries={countries} onDataChanged={onDataChanged}
        onEdit={(c) => { setModal(null); onEditCountry?.(c); }} />
      <ChangePasswordModal open={modal === "password"} onClose={() => setModal(null)} />
      <ConsultantModal open={modal === "consultant"} onClose={() => setModal(null)} />
      <InboxModal open={modal === "inbox"} onClose={() => setModal(null)} />
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-slate-200 transition-colors hover:bg-white/[0.06]">
      <Icon size={14} className="text-slate-400" /> {label}
    </button>
  );
}

function Shell({ open, onClose, title, children, testid }) {
  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div data-testid={testid}
            className="fixed inset-x-0 bottom-0 z-[61] mx-auto max-h-[88vh] max-w-lg overflow-y-auto rounded-t-3xl border-t border-white/10 p-5"
            style={{ background: "rgba(10,14,23,0.99)" }}
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 300 }}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }}>{title}</h2>
              <button data-testid="admin-modal-close" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-slate-300"><X size={15} /></button>
            </div>
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const BLANK = {
  code: "", name: "", flag: "", city: "", region: "", type: "Residency", currency: "",
  from_display: "", program: "", objective: "", tagline: "", accent: "#E2C275",
  categories: "", lat: "", lng: "",
  minimum_investment: "", residency_outcome: "", family: "", physical_presence: "", typical_pathway: "",
  tax_profile: "", real_estate: "", processing: "",
};

function AddDestinationModal({ open, onClose, onDataChanged }) {
  const [f, setF] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.code.trim() || !f.name.trim()) { toast.error("Code and name are required"); return; }
    setSaving(true);
    try {
      const payload = {
        code: f.code, name: f.name, flag: f.flag || "🏳️", city: f.city, region: f.region,
        type: f.type, currency: f.currency, from_display: f.from_display, program: f.program,
        objective: f.objective, tagline: f.tagline, accent: f.accent,
        categories: f.categories.split(",").map((s) => s.trim()).filter(Boolean),
        lat: parseFloat(f.lat) || 0, lng: parseFloat(f.lng) || 0,
        snapshot: {
          minimum_investment: f.minimum_investment, residency_outcome: f.residency_outcome,
          family: f.family, physical_presence: f.physical_presence, typical_pathway: f.typical_pathway,
        },
        tax_profile: f.tax_profile, real_estate: f.real_estate, processing: f.processing,
      };
      await axios.post(`${API}/countries`, payload);
      toast.success(`${f.name} added`);
      setF(BLANK);
      onDataChanged?.();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not add destination");
    } finally { setSaving(false); }
  };

  return (
    <Shell open={open} onClose={onClose} title="Add destination" testid="admin-add-modal">
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <Row label="Code (e.g. PT)"><input data-testid="add-code" value={f.code} onChange={(e) => set("code", e.target.value.toUpperCase())} className={inputCls} maxLength={3} /></Row>
          <Row label="Flag emoji"><input data-testid="add-flag" value={f.flag} onChange={(e) => set("flag", e.target.value)} className={inputCls} /></Row>
        </div>
        <Row label="Country name"><input data-testid="add-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Row>
        <div className="grid grid-cols-2 gap-2">
          <Row label="City"><input value={f.city} onChange={(e) => set("city", e.target.value)} className={inputCls} /></Row>
          <Row label="Region"><input value={f.region} onChange={(e) => set("region", e.target.value)} className={inputCls} /></Row>
          <Row label="Type">
            <select data-testid="add-type" value={f.type} onChange={(e) => set("type", e.target.value)} className={inputCls}>
              <option value="Residency">Residency</option><option value="Citizenship">Citizenship</option>
            </select>
          </Row>
          <Row label="Currency"><input value={f.currency} onChange={(e) => set("currency", e.target.value)} className={inputCls} /></Row>
          <Row label="Latitude"><input value={f.lat} onChange={(e) => set("lat", e.target.value)} className={inputCls} placeholder="e.g. 38.7" /></Row>
          <Row label="Longitude"><input value={f.lng} onChange={(e) => set("lng", e.target.value)} className={inputCls} placeholder="e.g. -9.1" /></Row>
        </div>
        <Row label="'From …' price"><input value={f.from_display} onChange={(e) => set("from_display", e.target.value)} className={inputCls} placeholder="From €250k" /></Row>
        <Row label="Programme"><input value={f.program} onChange={(e) => set("program", e.target.value)} className={inputCls} /></Row>
        <Row label="Objective"><input value={f.objective} onChange={(e) => set("objective", e.target.value)} className={inputCls} /></Row>
        <Row label="Tagline"><input value={f.tagline} onChange={(e) => set("tagline", e.target.value)} className={inputCls} /></Row>
        <div className="grid grid-cols-2 gap-2">
          <Row label="Accent colour"><input type="color" value={f.accent} onChange={(e) => set("accent", e.target.value)} className="h-9 w-full rounded-lg border border-white/10 bg-transparent" /></Row>
          <Row label="Processing"><input value={f.processing} onChange={(e) => set("processing", e.target.value)} className={inputCls} placeholder="~45 days" /></Row>
        </div>
        <Row label="Categories (comma separated)"><input value={f.categories} onChange={(e) => set("categories", e.target.value)} className={inputCls} placeholder="Golden Visa, Tax Haven" /></Row>
        <p className="pt-1 text-[10px] font-semibold uppercase tracking-wider text-amber-400/80">5-point snapshot</p>
        <Row label="Minimum investment"><input value={f.minimum_investment} onChange={(e) => set("minimum_investment", e.target.value)} className={inputCls} /></Row>
        <Row label="Residency outcome"><input value={f.residency_outcome} onChange={(e) => set("residency_outcome", e.target.value)} className={inputCls} /></Row>
        <Row label="Family"><input value={f.family} onChange={(e) => set("family", e.target.value)} className={inputCls} /></Row>
        <Row label="Physical presence"><input value={f.physical_presence} onChange={(e) => set("physical_presence", e.target.value)} className={inputCls} /></Row>
        <Row label="Typical pathway"><input value={f.typical_pathway} onChange={(e) => set("typical_pathway", e.target.value)} className={inputCls} /></Row>
        <Row label="Taxation profile"><input value={f.tax_profile} onChange={(e) => set("tax_profile", e.target.value)} className={inputCls} /></Row>
        <Row label="Real estate rights"><input value={f.real_estate} onChange={(e) => set("real_estate", e.target.value)} className={inputCls} /></Row>
        <button data-testid="add-submit" onClick={submit} disabled={saving}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-300 py-3 text-sm font-semibold text-[#07090E] active:scale-[0.98] disabled:opacity-60">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Create destination
        </button>
        <p className="pb-2 text-[10px] text-slate-500">A standard 8-step journey is auto-created; edit it later from the destination.</p>
      </div>
    </Shell>
  );
}

function BulkManagerModal({ open, onClose, countries, onDataChanged, onEdit }) {
  const [busy, setBusy] = useState(null);

  const toggleFeatured = async (c) => {
    setBusy(c.code);
    try { await axios.put(`${API}/countries/${c.code}`, { featured: !c.featured }); onDataChanged?.(); }
    catch { toast.error("Update failed"); }
    finally { setBusy(null); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This cannot be undone (except via history).`)) return;
    setBusy(c.code);
    try { await axios.delete(`${API}/countries/${c.code}`); toast.success(`${c.name} deleted`); onDataChanged?.(); }
    catch { toast.error("Delete failed"); }
    finally { setBusy(null); }
  };

  return (
    <Shell open={open} onClose={onClose} title={`Manage destinations (${countries.length})`} testid="admin-bulk-modal">
      <div className="space-y-2">
        {countries.map((c) => (
          <div key={c.code} data-testid={`bulk-row-${c.code}`} className="flex items-center gap-2 rounded-xl border border-white/[0.07] bg-white/[0.03] p-2.5">
            <span className="text-xl">{c.flag}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-white">{c.name}</p>
              <p className="text-[11px] text-slate-400">{c.code} · {c.type}</p>
            </div>
            <button data-testid={`bulk-feature-${c.code}`} onClick={() => toggleFeatured(c)} disabled={busy === c.code}
              title={c.featured ? "Featured" : "Not featured"}
              className="grid h-8 w-8 place-items-center rounded-lg border transition-colors"
              style={{ borderColor: c.featured ? "rgba(226,194,117,0.5)" : "rgba(255,255,255,0.1)", color: c.featured ? "#E2C275" : "#64748B" }}>
              <Star size={15} fill={c.featured ? "#E2C275" : "none"} />
            </button>
            <button data-testid={`bulk-edit-${c.code}`} onClick={() => onEdit(c)} className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-slate-300"><Pencil size={14} /></button>
            <button data-testid={`bulk-delete-${c.code}`} onClick={() => remove(c)} disabled={busy === c.code} className="grid h-8 w-8 place-items-center rounded-lg border border-red-400/30 text-red-300"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function ChangePasswordModal({ open, onClose }) {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [cf, setCf] = useState("");
  const [saving, setSaving] = useState(false); const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setErr(null);
    if (nw.length < 8) { setErr("New password must be at least 8 characters"); return; }
    if (nw !== cf) { setErr("New passwords do not match"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: cur, new_password: nw });
      toast.success("Password changed");
      setCur(""); setNw(""); setCf(""); onClose();
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not change password");
    } finally { setSaving(false); }
  };

  return (
    <Shell open={open} onClose={onClose} title="Change password" testid="admin-password-modal">
      <form onSubmit={submit} className="space-y-3 pb-2">
        <input data-testid="pw-current" type="password" placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} className={inputCls} required />
        <input data-testid="pw-new" type="password" placeholder="New password (min 8 chars)" value={nw} onChange={(e) => setNw(e.target.value)} className={inputCls} required />
        <input data-testid="pw-confirm" type="password" placeholder="Confirm new password" value={cf} onChange={(e) => setCf(e.target.value)} className={inputCls} required />
        {err && <p data-testid="pw-error" className="text-xs text-red-400">{err}</p>}
        <button data-testid="pw-submit" type="submit" disabled={saving}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-300 py-2.5 text-sm font-semibold text-[#07090E] active:scale-[0.98] disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />} Update password
        </button>
      </form>
    </Shell>
  );
}

function ConsultantModal({ open, onClose }) {
  const [f, setF] = useState({ name: "", title: "", email: "", mobile: "", photo_url: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/consultant`); setF(data); }
    catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/consultant`, { name: f.name, title: f.title, email: f.email, mobile: f.mobile, profile_url: f.profile_url, bio: f.bio });
      toast.success("Consultant updated");
      onClose();
    } catch { toast.error("Could not save"); } finally { setSaving(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/consultant/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("photo_url", data.photo_url);
      toast.success("Photo updated");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <Shell open={open} onClose={onClose} title="Consultant profile" testid="admin-consultant-modal">
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-amber-300" /></div>
      ) : (
        <div className="space-y-3 pb-2">
          <div className="flex items-center gap-3">
            {photoSrc(f.photo_url) ? (
              <img data-testid="consultant-photo-preview" src={photoSrc(f.photo_url)} alt="consultant" className="h-16 w-16 rounded-full object-cover ring-2 ring-amber-300/40" />
            ) : (
              <span className="grid h-16 w-16 place-items-center rounded-full bg-amber-300/20 text-xl font-bold text-amber-200">{(f.name || "?").charAt(0)}</span>
            )}
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-white/12 px-3 py-2 text-xs font-semibold text-slate-200">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} {uploading ? "Uploading…" : "Upload photo"}
              <input data-testid="consultant-photo-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} disabled={uploading} />
            </label>
          </div>
          <Row label="Name"><input data-testid="consultant-name" value={f.name || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} /></Row>
          <Row label="Title"><input data-testid="consultant-title" value={f.title || ""} onChange={(e) => set("title", e.target.value)} className={inputCls} /></Row>
          <Row label="Email"><input data-testid="consultant-email-input" value={f.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls} /></Row>
          <Row label="Mobile"><input data-testid="consultant-mobile-input" value={f.mobile || ""} onChange={(e) => set("mobile", e.target.value)} className={inputCls} /></Row>
          <Row label="Profile link"><input data-testid="consultant-profile-input" value={f.profile_url || ""} onChange={(e) => set("profile_url", e.target.value)} placeholder="https://…" className={inputCls} /></Row>
          <Row label="Bio"><textarea data-testid="consultant-bio-input" rows={4} value={f.bio || ""} onChange={(e) => set("bio", e.target.value)} className={inputCls} /></Row>
          <button data-testid="consultant-save" onClick={save} disabled={saving}
            className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-300 py-2.5 text-sm font-semibold text-[#07090E] active:scale-[0.98] disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <UserCog size={15} />} Save consultant
          </button>
        </div>
      )}
    </Shell>
  );
}

const ANSWER_TXT = { yes: ["Yes", "#34D399"], maybe: ["Maybe", "#FBBF24"], no: ["No", "#F87171"], null: ["Not assessed", "#94A3B8"] };

function InboxModal({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${API}/admin/coordination-requests`); setRows(data); }
    catch { setRows([]); } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const markHandled = async (r, e) => {
    e.stopPropagation();
    try {
      const next = r.status === "handled" ? "new" : "handled";
      await axios.patch(`${API}/admin/coordination-requests/${r.id}`, { status: next });
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, status: next } : x)));
    } catch { toast.error("Update failed"); }
  };

  return (
    <Shell open={open} onClose={onClose} title="Coordination requests" testid="admin-inbox-modal">
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-amber-300" /></div>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-slate-400">No coordination requests yet.</p>
      ) : (
        <div className="space-y-2 pb-2" data-testid="inbox-list">
          {rows.map((r) => {
            const isOpen = openId === r.id;
            const rd = r.readiness || {};
            const handled = r.status === "handled";
            return (
              <div key={r.id} data-testid={`inbox-row-${r.id}`} className="overflow-hidden rounded-xl border border-white/[0.07] bg-white/[0.03]">
                <button onClick={() => setOpenId(isOpen ? null : r.id)} className="flex w-full items-start gap-2 p-3 text-left">
                  <span className="text-xl">{r.steps?.[0] ? "🧭" : "📄"}</span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">{r.name} <span className="font-normal text-slate-400">· {r.country_name}</span></p>
                    <p className="truncate text-[11px] text-slate-400">{r.email}{r.mobile ? ` · ${r.mobile}` : ""}</p>
                    <div className="mt-1 flex flex-wrap gap-1.5 text-[10px]">
                      <span className="rounded-full px-2 py-0.5 font-semibold text-emerald-300" style={{ background: "rgba(52,211,153,0.12)" }}>{rd.ready_steps ?? 0}/{rd.total_steps ?? 0} ready</span>
                      <span className="rounded-full px-2 py-0.5 font-semibold text-emerald-300" style={{ background: "rgba(52,211,153,0.12)" }}>{rd.strengths ?? 0} strengths</span>
                      <span className="rounded-full px-2 py-0.5 font-semibold text-amber-300" style={{ background: "rgba(251,146,60,0.14)" }}>{(rd.flags || []).length} to discuss</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="rounded-full px-2 py-0.5 text-[9px] font-bold uppercase" style={{ color: handled ? "#34D399" : "#FBBF24", background: handled ? "rgba(52,211,153,0.14)" : "rgba(251,191,36,0.14)" }}>{handled ? "Handled" : "New"}</span>
                    <ChevronDown size={14} className={`text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-white/[0.06] p-3">
                    {(rd.flags || []).length > 0 && (
                      <div className="mb-3 rounded-lg border border-amber-400/20 bg-amber-400/5 p-2.5">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-300">Gaps to discuss</p>
                        <ul className="mt-1 space-y-0.5">
                          {rd.flags.map((fl, i) => (
                            <li key={i} className="text-[11px] text-slate-300">{fl.node_title} — {fl.requirement} <span className="font-semibold" style={{ color: fl.answer === "no" ? "#F87171" : "#FBBF24" }}>({fl.answer})</span></li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="space-y-2">
                      {(r.steps || []).map((s) => (
                        <div key={s.id}>
                          <p className="text-[12px] font-semibold text-slate-100">{s.id} · {s.title}</p>
                          <ul className="mt-1 space-y-0.5">
                            {s.requirements.map((rq, i) => {
                              const [lbl, col] = ANSWER_TXT[rq.answer ?? "null"] || ANSWER_TXT.null;
                              return (
                                <li key={i} className="flex items-center justify-between gap-2 text-[11px]">
                                  <span className="text-slate-400">{rq.label}</span>
                                  <span className="shrink-0 font-semibold" style={{ color: col }}>{lbl}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <a href={`mailto:${r.email}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/12 py-2 text-[11px] font-semibold text-slate-200"><Mail size={12} /> Email client</a>
                      {r.mobile && <a href={`tel:${r.mobile.replace(/\s/g, "")}`} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-white/12 py-2 text-[11px] font-semibold text-slate-200"><Phone size={12} /> Call</a>}
                      <button data-testid={`inbox-handle-${r.id}`} onClick={(e) => markHandled(r, e)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border py-2 text-[11px] font-semibold" style={{ borderColor: handled ? "rgba(148,163,184,0.3)" : "rgba(52,211,153,0.4)", color: handled ? "#94A3B8" : "#34D399" }}>
                        <CheckCircle2 size={12} /> {handled ? "Mark new" : "Mark handled"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Shell>
  );
}
